import React, { useCallback, useMemo, useState } from "react";

import { faCaretUp } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Paragraph from "antd/es/typography/Paragraph";
import cn from "classnames";

import Email from "@mapmycustomers/shared/types/email/Email";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button, ButtonProps } from "@mapmycustomers/ui";

import getAllEmailRecipients from "../util/getAllEmailRecipients";
import useGetEmailRecipientName from "../util/useGetEmailRecipientName";

import styles from "./EmailRecipientsParagraph.module.scss";

interface ChildEmailRecipientsProps {
  className?: string;
  email: Email;
  onCollapse: () => void;
  onExpand: () => void;
}
const EmailRecipientsParagraph: React.FC<ChildEmailRecipientsProps> = ({
  className,
  email,
  onCollapse,
  onExpand,
}) => {
  const getEmailRecipientName = useGetEmailRecipientName();

  const [ellipsis, setEllipsis] = useState(false);
  const [expanded, , , toggle] = useBoolean(false);

  const recipients = useMemo(() => {
    return getAllEmailRecipients(email)
      .map((recipientAddress) => getEmailRecipientName(recipientAddress))
      .join(", ");
  }, [email, getEmailRecipientName]);

  const handleActionButtonClick: ButtonProps["onClick"] = useCallback(
    (e) => {
      e.stopPropagation();

      if (expanded) {
        onCollapse();
      } else {
        onExpand();
      }

      toggle();
    },
    [expanded, toggle, onExpand, onCollapse]
  );

  const renderActionButton = (className?: string) => {
    return (
      <Button
        className={cn(styles.toggleIcon, className)}
        icon={<FontAwesomeIcon icon={faCaretUp} rotation={expanded ? undefined : 180} />}
        iconOnly
        onClick={handleActionButtonClick}
        size="small"
        type="control"
      />
    );
  };

  return (
    <div className={cn(styles.container, className)}>
      <Paragraph
        className={styles.paragraph}
        ellipsis={{
          expandable: true,
          onEllipsis: setEllipsis,
          rows: 3,
          symbol: renderActionButton(),
        }}
      >
        {recipients}
      </Paragraph>
      {!ellipsis && renderActionButton(styles.regularActionButton)}
    </div>
  );
};

export default EmailRecipientsParagraph;
